import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  MapPin,
  Pencil,
  Plus,
  Search,
  Trash2,
  Warehouse as WarehouseIcon,
  X,
} from 'lucide-react';
import { useUserProfile } from '../../context/UserProfileContext';
import { useOrgWarehouses, type WarehouseInput } from '../../hooks/useOrgWarehouses';
import { AddressSearch, type SelectedPlace } from '../../components/ui/AddressSearch';
import { CustomDropdown } from '../../components/ui/CustomDropdown';
import { ConfirmModal } from '../../components/admin/ConfirmModal';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import type { Warehouse, WarehouseType, WarehouseStatus } from '../../types/admin';

// ── Constants ───────────────────────────────────────────────────────────────

const WAREHOUSE_TYPE_LABELS: Record<WarehouseType, string> = {
  owned: 'Owned',
  rented: 'Rented',
  partner: 'Partner',
};

const WAREHOUSE_STATUS_LABELS: Record<WarehouseStatus, string> = {
  active: 'Active',
  inactive: 'Inactive',
};

const WAREHOUSE_STATUS_ORDER: Record<string, number> = { active: 0, inactive: 1 };

// ── Shared styles ───────────────────────────────────────────────────────────

const inputClass =
  'w-full h-11 px-3 rounded-lg border border-stone bg-white text-small text-midnight placeholder:text-slate focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20 transition-colors duration-150';
const labelClass = 'block text-[11px] font-bold uppercase tracking-widest text-slate';

// ── Status Badge ────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: WarehouseStatus }) {
  const colors: Record<WarehouseStatus, string> = {
    active: 'bg-emerald-50 text-emerald-700',
    inactive: 'bg-stone text-slate',
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${colors[status]}`}
    >
      {WAREHOUSE_STATUS_LABELS[status]}
    </span>
  );
}

// ── Warehouse Form ──────────────────────────────────────────────────────────

interface WarehouseFormProps {
  initial?: Warehouse;
  onClose: () => void;
  onSave: (input: WarehouseInput) => Promise<unknown>;
  onDelete?: () => void;
}

function WarehouseForm({ initial, onClose, onSave, onDelete }: WarehouseFormProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [selectedPlace, setSelectedPlace] = useState<SelectedPlace | null>(
    initial ? { address: initial.address, placeId: initial.placeId } : null,
  );
  const [note, setNote] = useState(initial?.note ?? '');
  const [type, setType] = useState<WarehouseType | ''>(initial?.type ?? '');
  const [status, setStatus] = useState<WarehouseStatus | ''>(initial?.status ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) { setError('Warehouse name is required.'); return; }
    if (!selectedPlace) { setError('Please select an address.'); return; }
    setError('');
    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        address: selectedPlace.address,
        placeId: selectedPlace.placeId,
        note: note.trim(),
        ...(type ? { type } : {}),
        ...(status ? { status } : {}),
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save warehouse.');
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div
        className="relative bg-white rounded-t-2xl px-4 pt-5 pb-8 max-h-[90vh] overflow-y-auto
                   md:rounded-2xl md:max-w-lg md:w-full md:max-h-[85vh] md:pb-6"
      >
        <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-h3 text-midnight">
            {initial ? 'Edit Warehouse' : 'Add Warehouse'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate hover:text-midnight hover:bg-mist transition-colors"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {error && (
            <div className="px-4 py-3 rounded-lg bg-rose/10 border border-rose/30 text-small text-rose">
              {error}
            </div>
          )}

          {/* Name */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="wh-name" className={labelClass}>
              Warehouse Name <span className="text-rose">*</span>
            </label>
            <input
              id="wh-name"
              type="text"
              autoComplete="off"
              required
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Main Street Depot"
              className={inputClass}
            />
          </div>

          {/* Address */}
          <AddressSearch
            id="wh-address"
            label="Warehouse Address"
            required
            value={selectedPlace}
            onChange={setSelectedPlace}
          />

          {/* Type + Status */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="wh-type" className={labelClass}>Type</label>
              <CustomDropdown
                id="wh-type"
                value={type}
                onChange={v => setType(v as WarehouseType | '')}
                options={[
                  { value: '', label: '—' },
                  ...(Object.entries(WAREHOUSE_TYPE_LABELS) as [WarehouseType, string][]).map(
                    ([k, v]) => ({ value: k, label: v }),
                  ),
                ]}
                placeholder="—"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="wh-status" className={labelClass}>Status</label>
              <CustomDropdown
                id="wh-status"
                value={status}
                onChange={v => setStatus(v as WarehouseStatus | '')}
                options={[
                  { value: '', label: '—' },
                  ...(Object.entries(WAREHOUSE_STATUS_LABELS) as [WarehouseStatus, string][]).map(
                    ([k, v]) => ({ value: k, label: v }),
                  ),
                ]}
                placeholder="—"
              />
            </div>
          </div>

          {/* Note */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="wh-note" className={labelClass}>
              Note{' '}
              <span className="text-slate font-normal normal-case tracking-normal">(optional)</span>
            </label>
            <textarea
              id="wh-note"
              rows={3}
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Any relevant details about this warehouse…"
              className="w-full px-3 py-2.5 rounded-lg border border-stone bg-white text-small text-midnight placeholder:text-slate resize-none focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20 transition-colors duration-150"
            />
          </div>

          <button
            type="submit"
            disabled={saving}
            className="h-11 rounded-lg bg-plum text-white text-[13px] font-semibold flex items-center justify-center gap-2 hover:bg-rose active:opacity-80 disabled:opacity-50 transition-colors duration-150 mt-1"
          >
            {saving ? 'Saving…' : initial ? 'Save Changes' : 'Add Warehouse'}
          </button>

          {initial && onDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="h-11 rounded-lg border border-rose/30 text-rose text-[13px] font-semibold flex items-center justify-center gap-2 hover:bg-rose/5 active:opacity-80 transition-colors duration-150"
            >
              <Trash2 size={14} /> Delete Warehouse
            </button>
          )}
        </form>
      </div>
    </div>
  );
}

// ── Warehouse Card ──────────────────────────────────────────────────────────

function WarehouseCard({
  warehouse,
  onEdit,
}: {
  warehouse: Warehouse;
  onEdit: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onEdit}
      className="w-full text-left bg-white border border-stone rounded-xl p-4 shadow-brand-sm flex flex-col gap-2 hover:border-plum/40 hover:shadow-brand-md active:bg-mist transition-all duration-150"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-9 h-9 rounded-lg bg-plum/10 flex items-center justify-center mt-0.5">
          <WarehouseIcon size={17} className="text-plum" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-small text-midnight leading-snug">{warehouse.name}</p>
            {warehouse.status && <StatusBadge status={warehouse.status} />}
            {warehouse.type && (
              <span className="text-[11px] text-slate">{WAREHOUSE_TYPE_LABELS[warehouse.type]}</span>
            )}
          </div>
          <div className="flex items-start gap-1.5 mt-1">
            <MapPin size={13} className="shrink-0 text-slate mt-0.5" />
            <p className="text-[13px] text-slate leading-snug">{warehouse.address}</p>
          </div>
          {warehouse.note && (
            <p className="text-[13px] text-slate mt-1.5 leading-snug italic">{warehouse.note}</p>
          )}
        </div>
        <Pencil size={14} className="shrink-0 text-slate mt-1" />
      </div>
    </button>
  );
}

// ── Inner content (guards org access, owns state) ───────────────────────────

function ManageWarehousesContent({ orgId }: { orgId: string }) {
  const navigate = useNavigate();
  const { activeOrg } = useUserProfile();
  const fleetEnabled = activeOrg?.featureFleetManagement ?? false;

  const {
    warehouses,
    loading,
    error,
    createWarehouse,
    updateWarehouse,
    deleteWarehouse,
  } = useOrgWarehouses(orgId);

  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<Warehouse | null>(null);
  const [deleting, setDeleting] = useState<Warehouse | null>(null);

  const filtered = useMemo(() => {
    let list = [...warehouses];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        w =>
          w.name.toLowerCase().includes(q) ||
          w.address.toLowerCase().includes(q) ||
          (w.note && w.note.toLowerCase().includes(q)),
      );
    }
    list.sort((a, b) => {
      const sa = WAREHOUSE_STATUS_ORDER[a.status ?? 'active'] ?? 0;
      const sb = WAREHOUSE_STATUS_ORDER[b.status ?? 'active'] ?? 0;
      if (sa !== sb) return sa - sb;
      return a.name.localeCompare(b.name);
    });
    return list;
  }, [warehouses, search]);

  return (
    <div className="px-4 pt-6 pb-8 md:px-8 md:max-w-3xl md:mx-auto">
      {/* Breadcrumbs */}
      <nav className="flex items-center gap-1.5 text-xs text-secondary-theme mb-2">
        <button
          type="button"
          onClick={() => navigate('/admin')}
          className="hover:text-plum transition-colors"
        >
          Admin Panel
        </button>
        <span>&gt;</span>
        <button
          type="button"
          onClick={() => navigate('/admin/operations')}
          className="hover:text-plum transition-colors"
        >
          Operations
        </button>
      </nav>

      <h1 className="font-display text-h1 text-midnight mb-1">Manage Warehouses</h1>
      <p className="text-[13px] text-slate mb-6 leading-snug">
        Manage your warehouse locations. Warehouses are used to calculate travel times and
        coordinate crew dispatch.
      </p>

      {loading && (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {!loading && (
        <>
          {/* Search — only shown once there's something to search */}
          {warehouses.length > 0 && (
            <div className="relative mb-3">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search warehouses…"
                className="w-full h-10 pl-9 pr-3 rounded-lg border border-stone bg-white text-small text-midnight placeholder:text-slate focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20 transition-colors duration-150"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate hover:text-midnight"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          )}

          {/* Add button or upgrade callout */}
          {!fleetEnabled && warehouses.length >= 1 ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 mb-4">
              <p className="text-[13px] font-semibold text-amber-800 mb-1">
                Warehouse limit reached
              </p>
              <p className="text-[12px] text-amber-700 leading-relaxed mb-2">
                Your current plan supports one warehouse. Upgrade your plan to add more warehouses
                and other fleet management features.
              </p>
              <button
                type="button"
                onClick={() => navigate('/admin/company/billing/manage-plan')}
                className="flex items-center gap-1.5 text-[12px] font-semibold text-plum hover:underline"
              >
                Upgrade Plan <ArrowRight size={13} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowAdd(true)}
              className="w-full h-11 rounded-xl border-2 border-dashed border-stone flex items-center justify-center gap-2 text-small font-semibold text-slate hover:border-plum hover:text-plum active:bg-mist transition-colors duration-150 mb-4"
            >
              <Plus size={16} /> Add Warehouse
            </button>
          )}

          {/* Count label */}
          {warehouses.length > 0 && (
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-3">
              Warehouses ({filtered.length})
            </p>
          )}

          {/* Error state */}
          {error && (
            <div className="flex flex-col items-center py-8 gap-2 text-center">
              <p className="text-small text-rose font-semibold">Failed to load warehouses</p>
              <p className="text-small text-slate">{error}</p>
            </div>
          )}

          {/* Empty — nothing added yet */}
          {!error && warehouses.length === 0 && (
            <div className="flex flex-col items-center py-12 gap-3 text-center">
              <div className="w-14 h-14 rounded-full bg-mist flex items-center justify-center">
                <WarehouseIcon size={24} className="text-slate" />
              </div>
              <p className="text-small text-slate">No warehouses yet. Add one above.</p>
            </div>
          )}

          {/* Empty — search returned nothing */}
          {!error && warehouses.length > 0 && filtered.length === 0 && (
            <p className="text-small text-slate text-center py-8">
              No warehouses match your search.
            </p>
          )}

          {/* Warehouse list */}
          {!error && filtered.length > 0 && (
            <ul className="flex flex-col gap-3">
              {filtered.map(wh => (
                <li key={wh.id}>
                  <WarehouseCard warehouse={wh} onEdit={() => setEditing(wh)} />
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {/* ── Modals ──────────────────────────────────────────────── */}

      {showAdd && (
        <WarehouseForm onClose={() => setShowAdd(false)} onSave={createWarehouse} />
      )}

      {editing && (
        <WarehouseForm
          initial={editing}
          onClose={() => setEditing(null)}
          onSave={input => updateWarehouse(editing.id, input)}
          onDelete={() => {
            setDeleting(editing);
            setEditing(null);
          }}
        />
      )}

      <ConfirmModal
        isOpen={!!deleting}
        title="Delete Warehouse"
        message={`Are you sure you want to delete "${deleting?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => {
          if (deleting) deleteWarehouse(deleting.id);
          setDeleting(null);
        }}
        onClose={() => setDeleting(null)}
      />
    </div>
  );
}

// ── Page (guards org admin access) ───────────────────────────────────────────

export function ManageWarehousesPage() {
  const { adminOrg, loading } = useUserProfile();

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!adminOrg) {
    return (
      <div className="px-4 pt-6 flex flex-col items-center py-20 gap-3 text-center">
        <p className="text-small text-slate">You don't have org admin access.</p>
      </div>
    );
  }

  return <ManageWarehousesContent orgId={adminOrg.orgId} />;
}
